<template>
  <div class="dt">
    <!-- 筛选区 -->
    <div v-if="$slots.filters" class="dt__filters">
      <slot name="filters" />
    </div>

    <div class="dt__body" :class="`is-${density}`">
      <!-- 工具栏 -->
      <div class="dt__toolbar">
        <div class="dt__toolbar-left">
          <slot name="toolbar-left">
            <span v-if="selectable && selectedCount" class="dt__hint">
              已选 <b>{{ selectedCount }}</b> 项
              <button class="dt__link" @click="$emit('clear-selection')">取消选择</button>
            </span>
            <span v-else class="dt__hint">共 {{ total }} 条</span>
          </slot>
        </div>

        <div class="dt__toolbar-right">
          <slot name="toolbar-actions" />

          <el-dropdown trigger="click" placement="bottom-end" @command="setDensity">
            <button class="dt__tool">密度</button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="loose" :disabled="density === 'loose'">宽松</el-dropdown-item>
                <el-dropdown-item command="default" :disabled="density === 'default'">标准</el-dropdown-item>
                <el-dropdown-item command="compact" :disabled="density === 'compact'">紧凑</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <el-popover trigger="click" placement="bottom-end" width="180">
            <template #reference>
              <button class="dt__tool">列设置</button>
            </template>
            <div class="dt__cols">
              <el-checkbox
                v-for="col in columns"
                :key="col.prop"
                :model-value="!hidden.includes(col.prop)"
                :disabled="col.fixed"
                @change="v => toggleCol(col.prop, v)"
              >
                {{ col.label }}
              </el-checkbox>
            </div>
          </el-popover>

          <button class="dt__tool" @click="$emit('refresh')">刷新</button>
        </div>
      </div>

      <!-- 表格 -->
      <el-table
        ref="tableRef"
        :data="data"
        :loading="loading"
        :row-key="rowKey"
        :border="false"
        :stripe="false"
        style="width: 100%"
        @selection-change="rows => $emit('selection-change', rows)"
      >
        <el-table-column v-if="selectable" type="selection" width="44" :reserve-selection="!!rowKey" />

        <template v-for="col in visibleColumns" :key="col.prop">
          <el-table-column
            :prop="col.prop"
            :label="col.label"
            :width="col.width"
            :min-width="col.minWidth"
            :align="col.align || (col.type === 'money' ? 'right' : 'left')"
            :sortable="col.sortable"
            :show-overflow-tooltip="col.ellipsis !== false"
          >
            <template #default="scope">
              <slot :name="`cell-${col.prop}`" :row="scope.row" :index="scope.$index">
                <span :class="cellClass(col)">{{ display(col, scope.row) }}</span>
              </slot>
            </template>
          </el-table-column>
        </template>

        <el-table-column v-if="$slots.ops" label="操作" :width="opsWidth" align="right" class-name="dt__ops">
          <template #default="scope">
            <slot name="ops" :row="scope.row" :index="scope.$index" />
          </template>
        </el-table-column>

        <template #empty>
          <slot name="empty">
            <div class="dt__empty">{{ emptyText }}</div>
          </slot>
        </template>
      </el-table>

      <!-- 分页 -->
      <div v-if="total > 0" class="dt__foot">
        <span class="dt__hint">共 {{ total }} 条 · 每页 {{ pageSize }} 条</span>
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="prev, pager, next, sizes"
          background
          @current-change="p => $emit('update:page', p)"
          @size-change="s => $emit('update:pageSize', s)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'

const DENSITY_KEY = 'rbf_table_density'

export default defineComponent({
  name: 'DataTable',
  props: {
    columns: { type: Array, required: true },
    data: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 20 },
    selectable: { type: Boolean, default: false },
    selectedCount: { type: Number, default: 0 },
    rowKey: { type: String, default: '' },
    opsWidth: { type: [String, Number], default: 180 },
    emptyText: { type: String, default: '暂无数据' }
  },
  emits: ['update:page', 'update:pageSize', 'selection-change', 'clear-selection', 'refresh'],
  setup(props) {
    const tableRef = ref(null)
    const hidden = ref([])
    const density = ref(localStorage.getItem(DENSITY_KEY) || 'default')

    const visibleColumns = computed(() =>
      props.columns.filter(c => !hidden.value.includes(c.prop))
    )

    const setDensity = (v) => {
      density.value = v
      localStorage.setItem(DENSITY_KEY, v)
    }

    const toggleCol = (prop, visible) => {
      if (visible) hidden.value = hidden.value.filter(p => p !== prop)
      else if (!hidden.value.includes(prop)) hidden.value.push(prop)
    }

    const cellClass = (col) => {
      if (col.type === 'money' || col.type === 'number') return 'num'
      return ''
    }

    const display = (col, row) => {
      const raw = row[col.prop]
      if (col.formatter) return col.formatter(row)
      if (raw === null || raw === undefined || raw === '') return '—'
      return raw
    }

    return {
      tableRef, hidden, density, visibleColumns,
      setDensity, toggleCol, cellClass, display
    }
  }
})
</script>

<style scoped>
.dt {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dt__filters {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 12px 16px;
}

.dt__body {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
}

/* ---------- 工具栏 ---------- */
.dt__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border);
}

.dt__toolbar-left {
  min-width: 0;
}

.dt__toolbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
}

.dt__hint {
  font-size: 12px;
  color: var(--text-secondary);
}

.dt__hint b {
  color: var(--brand-500);
  font-weight: 500;
}

.dt__link {
  margin-left: 8px;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  color: var(--brand-500);
  cursor: pointer;
}

.dt__link:hover {
  text-decoration: underline;
}

.dt__tool {
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  color: var(--text-secondary);
  cursor: pointer;
}

.dt__tool:hover {
  color: var(--brand-500);
}

.dt__cols {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 300px;
  overflow-y: auto;
}

.dt__cols :deep(.el-checkbox) {
  height: 26px;
  margin-right: 0;
}

/* ---------- 表格 ---------- */
.dt__body :deep(.el-table) {
  font-size: 13px;
}

.dt__body :deep(.el-table th.el-table__cell) {
  background: var(--bg-subtle);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  padding: 9px 0;
}

.dt__body :deep(.el-table td.el-table__cell) {
  border-bottom: 1px solid var(--bg-page);
}

.dt__body :deep(.el-table__body tr:hover > td.el-table__cell) {
  background: var(--bg-hover);
}

.dt__body :deep(.el-table__body tr.current-row > td.el-table__cell) {
  background: var(--bg-selected);
}

/* 单元格纵向留白：紧凑 8 / 标准 12 / 宽松 16 */
.dt__body :deep(.el-table td.el-table__cell) {
  padding: 12px 0;
}
.dt__body.is-compact :deep(.el-table td.el-table__cell) {
  padding: 7px 0;
}
.dt__body.is-compact :deep(.el-table th.el-table__cell) {
  padding: 7px 0;
}
.dt__body.is-loose :deep(.el-table td.el-table__cell) {
  padding: 17px 0;
}

/* 首末列内边距（el-table 用 cell 而非 padding 控制） */
.dt__body :deep(.el-table .cell) {
  padding-left: 16px;
  padding-right: 16px;
}

/* 去掉表格外层多余边框线 */
.dt__body :deep(.el-table::before),
.dt__body :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.dt__empty {
  padding: 44px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
}

/* 操作列：文字按钮，最多 3 个 */
.dt__ops .cell {
  white-space: nowrap;
}

:deep(.dt-op) {
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  color: var(--brand-500);
  cursor: pointer;
}

:deep(.dt-op):hover {
  text-decoration: underline;
}

:deep(.dt-op.is-danger) {
  color: var(--danger-text);
}

:deep(.dt-sep) {
  color: var(--text-disabled);
  margin: 0 6px;
}

/* ---------- 分页 ---------- */
.dt__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
}

.dt__foot :deep(.el-pagination) {
  --el-pagination-button-bg-color: var(--bg-card);
  --el-pagination-hover-color: var(--brand-500);
  font-size: 12px;
}

.dt__foot :deep(.el-pagination.is-background .el-pager li:not(.is-disabled).is-active) {
  background: var(--brand-500);
  color: #fff;
}

.dt__foot :deep(.el-pagination .el-pagination__sizes) {
  margin-left: 8px;
}
</style>
